import DefaultLayout from "@/layouts/DefaultLayout";
import KTruckForm from "kinnect-front/KYard/Trucks/KTruckForm";
import { Crudable, FetchesData, Pageable } from "kinnect-mixins";
import { KActionDialog, KRoutableTreeview } from "kinnect-general";
import { YardTrip, YardTruck } from "kinnect-models";
import { formatDate, formatTime } from "kinnect-formatters";
import { getPreTrips } from "@/utils/http-calls";

export default {
	name: "YardTruckPage",

	metaInfo() {
		const title = this.editing && this.fetched
			? `Yard - Trucks - ${this.truck.name}`
			: "Yard - Trucks - Add a Truck";

		return { title: this.fetched ? title : null };
	},

	props: { idTruck: Number },

	mixins: [
		Crudable,
		FetchesData,
		Pageable
	],

	data: () => ( {
		crud: "yard_trucks",
		deleteDialogOpen: false,
		truck: null
	} ),

	computed: {
		editing() {
			return Boolean( this.idTruck );
		},

		canBrowseYardPreTrips() {
			return this.$can( "browse_yard_pre_trips" );
		},

		genDeleteDialog() {
			if ( this.editing && this.truck && this.canDelete ) {
				return <KActionDialog
					confirmButtonColor="error"
					confirmButtonText="Delete"
					loading={this.$wait.is( "delete truck" )}
					onConfirm={() => this.truck.delete()}
					redirect={[ "back", { name: "yardTrucks" } ]}
					title="Delete Truck"
					vModel={this.deleteDialogOpen}
				>
					Are you sure you want to delete the truck <strong>{this.truck.name}</strong>?
				</KActionDialog>;
			}
		},

		genPreTripsTree() {
			return this.canBrowseYardPreTrips && <KRoutableTreeview
				itemIcon="list"
				loadChildren={this.fetchTruckPreTrips}
				on={{
					"click:item": preTrip => this.$router.push( {
						name: "yardTruckPreTrip",
						params: {
							idTruck: preTrip.idTruck,
							idPreTrip: preTrip.id
						}
					} )
				}}
				scopedSlots={{
					name: preTrip => `${formatDate( preTrip.created_at )} at ${formatTime( preTrip.created_at )}`
				}}
				title="Pre-Trips"
			></KRoutableTreeview>;
		},

		genActiveTripsTree() {
			return this.$can( "browse_yard_trips" ) && <KRoutableTreeview
				itemIcon="directions"
				loadChildren={this.fetchTruckActiveTrips}
				on={{
					"click:item": trip => this.$router.push( {
						name: "yardTrip",
						params: { idTrip: trip.id }
					} )
				}}
				scopedSlots={{
					name: trip => `${formatDate( trip.deliveryDate, "MMM d" )}, ${formatTime( trip.estDepartureTime )} - ${formatTime( trip.estReturnTime )}`
				}}
				title="Active Trips"
			></KRoutableTreeview>;
		},

		genCompletedTripsTree() {
			return this.$can( "browse_yard_trips" ) && <KRoutableTreeview
				itemIcon="directions"
				loadChildren={this.fetchTruckCompletedTrips}
				on={{
					"click:item": trip => this.$router.push( {
						name: "yardTrip",
						params: { idTrip: trip.id }
					} )
				}}
				scopedSlots={{
					name: trip => `${formatDate( trip.deliveryDate, "MMM d" )}, ${formatTime( trip.estDepartureTime )} - ${formatTime( trip.estReturnTime )}`
				}}
				title="Completed Trips"
			></KRoutableTreeview>;
		},

		genSidebar() {
			if ( !this.editing ) {
				return;
			}

			return <template slot="leftSidebar">
				{this.genPreTripsTree}
				{this.genActiveTripsTree}
				{this.genCompletedTripsTree}
			</template>;
		},

		toolbarActions() {
			const actions = [];

			if ( this.editing && this.canDelete ) {
				actions.push( {
					icon: "delete",
					onClick: () => this.deleteDialogOpen = true,
					text: "Delete Truck",
					tooltip: "Delete this truck"
				} );
			}

			if ( this.editing && this.$can( "add_yard_pre_trips" ) ) {
				actions.push( {
					icon: "add_circle",
					to: {
						name: "yardTruckPreTripAdd",
						params: { idTruck: this.idTruck }
					},
					text: "Add Pre-Trip",
					tooltip: "Add a new pre-trip for this truck"
				} );
			}

			return actions;
		}
	},

	methods: {
		async fetchTruckPreTrips() {
			if ( this.editing ) {
				const response = await getPreTrips( this.idTruck );

				return response;
			}
		},

		fetchTruckActiveTrips() {
			if ( this.editing ) {
				return YardTrip
				.where( "complete", false )
				.where( "idTruck", this.truck.id )
				.get();
			}
		},

		fetchTruckCompletedTrips() {
			if ( this.editing ) {
				return YardTrip
				.where( "complete", true )
				.where( "idTruck", this.truck.id )
				.get();
			}
		},

		async fetchTruck() {
			if ( this.editing ) {
				this.truck = await YardTruck.findPageModel( this.idTruck );
			}
		},

		async fetch() {
			await this.fetchTruck();
		}
	},

	render() {
		return <DefaultLayout maxWidth={this.editing ? "900px" : "600px"}>
			<KTruckForm
				loading={this.$wait.is( "find truck" )}
				readonly={this.editing ? !this.canEdit : !this.canAdd}
				truck={this.truck}
			></KTruckForm>

			{this.genDeleteDialog}

			{this.genSidebar}
		</DefaultLayout>;
	}
};
